import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CalendarNavigationDirection,
  CalendarSlideDirection,
  HoveredMonth,
  MonthState,
  UseDateRangePickerParams,
  UseDateRangePickerResult,
} from '../types';
import { calculateDateRange, formatDateDisplay } from '../utils';

export function useDateRangePicker({
  fromDate,
  toDate,
  onDateChange,
  disabled,
  disableFutureDays,
}: UseDateRangePickerParams): UseDateRangePickerResult {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRangeType, setSelectedRangeType] = useState<string>('custom');
  const [fromError, setFromError] = useState<string | undefined>();
  const [toError, setToError] = useState<string | undefined>();
  const [hoveredMonth, setHoveredMonth] = useState<HoveredMonth | null>(null);

  const getInitialFromYear = useCallback(() => {
    const currentYear = new Date().getFullYear();
    if (fromDate) {
      try {
        return NonTimezoneSensitiveDate.fromISO8601String(fromDate).getYear();
      } catch {
        if (toDate) {
          try {
            return NonTimezoneSensitiveDate.fromISO8601String(toDate).getYear();
          } catch {
            return currentYear;
          }
        }
        return currentYear;
      }
    }

    if (toDate) {
      try {
        return NonTimezoneSensitiveDate.fromISO8601String(toDate).getYear();
      } catch {
        return currentYear;
      }
    }

    return currentYear;
  }, [fromDate, toDate]);

  const [fromYear, setFromYear] = useState<number>(getInitialFromYear);
  const toYear = fromYear + 1;
  const [prevFromYear, setPrevFromYear] = useState<number | null>(null);
  const [prevToYear, setPrevToYear] = useState<number | null>(null);
  const [slideDirection, setSlideDirection] =
    useState<CalendarSlideDirection | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const prevFromYearRef = useRef<number>(getInitialFromYear());
  const isAnimatingRef = useRef<boolean>(false);
  const fromYearRef = useRef<number>(getInitialFromYear());
  const prevToDateYearRef = useRef<number | null>(null);
  const prevFromDateYearRef = useRef<number | null>(null);
  const today = useMemo(() => NonTimezoneSensitiveDate.now(), []);

  const clearAllSelection = useCallback(() => {
    setHoveredMonth(null);
    setFromError(undefined);
    setToError(undefined);
    setSelectedRangeType('custom');
    onDateChange(undefined, undefined);
  }, [onDateChange]);

  const clearToSelection = useCallback(() => {
    setHoveredMonth(null);
    setToError(undefined);
    setSelectedRangeType('custom');
    onDateChange(fromDate, undefined);
  }, [fromDate, onDateChange]);

  const parsedFromDate = useMemo(() => {
    if (!fromDate) return null;
    try {
      return NonTimezoneSensitiveDate.fromISO8601String(fromDate);
    } catch {
      return null;
    }
  }, [fromDate]);

  const parsedToDate = useMemo(() => {
    if (!toDate) return null;
    try {
      return NonTimezoneSensitiveDate.fromISO8601String(toDate);
    } catch {
      return null;
    }
  }, [toDate]);

  const activeRange = useMemo(() => {
    const hoveredStart =
      hoveredMonth !== null
        ? new NonTimezoneSensitiveDate(hoveredMonth.year, hoveredMonth.month, 1)
        : null;
    const hoveredRangeStart = hoveredStart?.startOf('month') ?? null;
    const hoveredRangeEnd = hoveredStart?.endOf('month') ?? null;

    const fromStart = parsedFromDate?.startOf('month') ?? null;
    const fromEnd = parsedFromDate?.endOf('month') ?? null;
    const toStart = parsedToDate?.startOf('month') ?? null;
    const toEnd = parsedToDate?.endOf('month') ?? null;

    // Check if the range is invalid (toDate < fromDate)
    const isInvalidRange =
      parsedFromDate && parsedToDate && parsedToDate.lt(parsedFromDate);

    if (fromStart && toEnd) {
      // Don't show range if it's invalid (backward)
      if (isInvalidRange) {
        // Only show the fromDate range when invalid
        return {
          start: fromStart,
          end: fromEnd ?? fromStart.endOf('month'),
          isPreview: false,
        };
      }

      let start = fromStart;
      let end = toEnd;

      if (end.lt(start)) {
        const fallbackStart = toStart ?? end.startOf('month');
        const fallbackEnd = fromEnd ?? start.endOf('month');
        start = fallbackStart;
        end = fallbackEnd;
      }

      return {
        start,
        end,
        isPreview: false,
      };
    }

    if (fromStart) {
      if (hoveredRangeStart && hoveredRangeEnd) {
        if (hoveredRangeStart.lt(fromStart)) {
          return {
            start: fromStart,
            end: fromEnd ?? fromStart.endOf('month'),
            isPreview: false,
          };
        }

        return {
          start: fromStart,
          end: hoveredRangeEnd,
          isPreview: true,
        };
      }

      return {
        start: fromStart,
        end: fromEnd ?? fromStart.endOf('month'),
        isPreview: false,
      };
    }

    if (toStart) {
      if (hoveredRangeStart && hoveredRangeEnd) {
        if (hoveredRangeEnd.lt(toStart)) {
          return {
            start: hoveredRangeStart,
            end: toEnd ?? toStart.endOf('month'),
            isPreview: true,
          };
        }

        return {
          start: toStart,
          end: hoveredRangeEnd,
          isPreview: true,
        };
      }

      return {
        start: toStart,
        end: toEnd ?? toStart.endOf('month'),
        isPreview: false,
      };
    }

    if (hoveredRangeStart && hoveredRangeEnd) {
      return {
        start: hoveredRangeStart,
        end: hoveredRangeEnd,
        isPreview: true,
      };
    }

    return {
      start: null,
      end: null,
      isPreview: false,
    };
  }, [hoveredMonth, parsedFromDate, parsedToDate]);

  useEffect(() => {
    if (disabled) {
      setHoveredMonth(null);
    }
  }, [disabled]);

  useEffect(() => {
    fromYearRef.current = fromYear;
  }, [fromYear]);

  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;

    try {
      if (fromDate) {
        const year =
          NonTimezoneSensitiveDate.fromISO8601String(fromDate).getYear();
        const currentFromYear = fromYearRef.current;
        const currentToYear = currentFromYear + 1;
        const isYearVisible =
          year === currentFromYear || year === currentToYear;

        if (!isYearVisible) {
          prevFromYearRef.current = year;
          prevFromDateYearRef.current = year;
          fromYearRef.current = year;
          setFromYear(year);
        } else {
          prevFromDateYearRef.current = year;
        }
      } else if (toDate) {
        const year =
          NonTimezoneSensitiveDate.fromISO8601String(toDate).getYear();
        const targetFromYear = year - 1;
        const currentFromYear = fromYearRef.current;
        const currentToYear = currentFromYear + 1;
        const isYearVisible =
          year === currentFromYear || year === currentToYear;

        if (!isYearVisible) {
          prevFromYearRef.current = targetFromYear;
          prevToDateYearRef.current = year;
          fromYearRef.current = targetFromYear;
          setFromYear(targetFromYear);
        } else {
          prevToDateYearRef.current = year;
        }
      }
    } catch {
      // Ignore invalid initial dates
    }
  }, [fromDate, toDate]);

  const animateToYear = useCallback(
    (targetYear: number, directionOverride?: CalendarSlideDirection) => {
      if (isAnimatingRef.current) return;
      const currentFromYear = fromYearRef.current;
      if (currentFromYear === targetYear && !directionOverride) {
        return;
      }

      setHoveredMonth(null);
      isAnimatingRef.current = true;
      setIsAnimating(true);
      setPrevFromYear(currentFromYear);
      setPrevToYear(currentFromYear + 1);

      let direction: CalendarSlideDirection;
      if (directionOverride) {
        direction = directionOverride;
      } else {
        direction = targetYear > currentFromYear ? 'left' : 'right';
      }
      setSlideDirection(direction);

      setFromYear(targetYear);
      fromYearRef.current = targetYear;
      prevFromYearRef.current = targetYear;

      setTimeout(() => {
        setPrevFromYear(null);
        setPrevToYear(null);
        setSlideDirection(null);
        setIsAnimating(false);
        isAnimatingRef.current = false;
      }, 300);
    },
    []
  );

  useEffect(() => {
    if (!hasMountedRef.current) return;

    if (fromDate && !isAnimatingRef.current) {
      try {
        const year =
          NonTimezoneSensitiveDate.fromISO8601String(fromDate).getYear();
        const currentFromYear = fromYearRef.current;
        const currentToYear = currentFromYear + 1;
        const isYearVisible =
          year === currentFromYear || year === currentToYear;

        if (!isYearVisible) {
          let direction: CalendarSlideDirection | undefined;
          if (prevFromDateYearRef.current !== null) {
            direction = year > prevFromDateYearRef.current ? 'left' : 'right';
          }

          prevFromYearRef.current = year;
          prevFromDateYearRef.current = year;
          animateToYear(year, direction);
        } else {
          prevFromDateYearRef.current = year;
        }
      } catch {
        // Ignore invalid dates
      }
    } else if (!fromDate && !toDate) {
      prevFromDateYearRef.current = null;
    } else if (!fromDate && toDate) {
      prevFromDateYearRef.current = null;
    }
  }, [fromDate, toDate, animateToYear]);

  useEffect(() => {
    if (!hasMountedRef.current) return;

    if (toDate && !fromDate && !isAnimatingRef.current) {
      try {
        const year =
          NonTimezoneSensitiveDate.fromISO8601String(toDate).getYear();
        const targetFromYear = year - 1;
        const currentFromYear = fromYearRef.current;
        const currentToYear = currentFromYear + 1;
        const isYearVisible =
          year === currentFromYear || year === currentToYear;

        if (!isYearVisible) {
          let direction: CalendarSlideDirection | undefined;
          if (prevToDateYearRef.current !== null) {
            direction = year > prevToDateYearRef.current ? 'left' : 'right';
          }

          prevFromYearRef.current = targetFromYear;
          prevToDateYearRef.current = year;
          animateToYear(targetFromYear, direction);
        } else {
          prevToDateYearRef.current = year;
        }
      } catch {
        // Ignore invalid dates
      }
    } else if (!toDate) {
      prevToDateYearRef.current = null;
    } else if (fromDate) {
      try {
        const year =
          NonTimezoneSensitiveDate.fromISO8601String(toDate).getYear();
        prevToDateYearRef.current = year;
      } catch {
        // Ignore invalid dates
      }
    }
  }, [toDate, fromDate, animateToYear]);

  const onPredefinedRangeChange = useCallback(
    (value: string) => {
      setSelectedRangeType(value);
      if (value === 'custom') {
        return;
      }

      const range = calculateDateRange(value);
      onDateChange(range.from_date, range.to_date);
    },
    [onDateChange]
  );

  const onFromInputChange = useCallback(
    (value: string | undefined) => {
      if (!value) {
        // When from date is cleared, clear both from and to dates
        clearAllSelection();
        return;
      }

      // DateField already validates the date, so we just need to check range constraints
      // Still update the date even if invalid, so user can see what they typed
      onDateChange(value, toDate);
      setSelectedRangeType('custom');

      // Validate and set error if needed
      if (toDate && value > toDate) {
        setFromError('From date cannot be after To date');
      } else {
        setFromError(undefined);
      }
    },
    [toDate, onDateChange, clearAllSelection]
  );

  const onToInputChange = useCallback(
    (value: string | undefined) => {
      if (!value) {
        // When to date is cleared, only clear the to date
        clearToSelection();
        return;
      }

      // DateField already validates the date, so we just need to check range constraints
      // Still update the date even if invalid, so user can see what they typed
      onDateChange(fromDate, value);
      setSelectedRangeType('custom');

      // Validate and set error if needed
      if (fromDate && value < fromDate) {
        setToError('To date cannot be before From date');
        // Don't animate calendar for invalid ranges
        return;
      } else {
        setToError(undefined);
      }

      // Animate calendar to show the toDate year when user types a valid date
      // Only animate if the range is valid (toDate >= fromDate)
      try {
        const year =
          NonTimezoneSensitiveDate.fromISO8601String(value).getYear();
        // Show year-1 on left, year on right
        animateToYear(year - 1);
      } catch {
        // Ignore invalid dates
      }
    },
    [fromDate, onDateChange, clearToSelection, animateToYear]
  );

  const onFromInputFocus = useCallback(
    (_e: React.FocusEvent<HTMLDivElement>) => {
      if (fromDate) {
        try {
          const year =
            NonTimezoneSensitiveDate.fromISO8601String(fromDate).getYear();
          animateToYear(year);
        } catch {
          // Ignore invalid dates
        }
      }
    },
    [fromDate, animateToYear]
  );

  const onFromInputBlur = useCallback(
    (_e: React.FocusEvent<HTMLDivElement>) => {
      // Validate range constraint on blur
      if (fromDate && toDate && fromDate > toDate) {
        setFromError('From date cannot be after To date');
      } else {
        setFromError(undefined);
      }
    },
    [fromDate, toDate]
  );

  const onToInputFocus = useCallback(
    (_e: React.FocusEvent<HTMLDivElement>) => {
      if (toDate) {
        try {
          const year =
            NonTimezoneSensitiveDate.fromISO8601String(toDate).getYear();
          animateToYear(year - 1);
        } catch {
          // Ignore invalid dates
        }
      }
    },
    [toDate, animateToYear]
  );

  const onToInputBlur = useCallback(
    (_e: React.FocusEvent<HTMLDivElement>) => {
      // Validate range constraint on blur
      if (fromDate && toDate && toDate < fromDate) {
        setToError('To date cannot be before From date');
      } else {
        setToError(undefined);
      }
    },
    [fromDate, toDate]
  );

  const onMonthClick = useCallback(
    (year: number, month: number, isMonthDisabled?: boolean) => {
      if (disabled || isMonthDisabled) {
        return;
      }

      const monthStart = new NonTimezoneSensitiveDate(year, month, 1);
      const monthEnd = monthStart.endOf('month');
      const monthStartISO = monthStart.toISO8601String();
      const monthEndISO = monthEnd.toISO8601String();

      const hasCompleteRange = fromDate && toDate;

      if (hasCompleteRange) {
        onDateChange(monthStartISO, undefined);
      } else if (fromDate && !toDate) {
        const from = NonTimezoneSensitiveDate.fromISO8601String(fromDate);
        const fromMonthStart = from.startOf('month').toISO8601String();

        if (monthStartISO < fromMonthStart) {
          onDateChange(monthStartISO, undefined);
        } else {
          onDateChange(fromDate, monthEndISO);
        }
      } else {
        onDateChange(monthStartISO, undefined);
      }
      setHoveredMonth(null);
      setSelectedRangeType('custom');
    },
    [disabled, fromDate, toDate, onDateChange]
  );

  const getMonthState = useCallback(
    (year: number, month: number): MonthState => {
      const monthStart = new NonTimezoneSensitiveDate(year, month, 1);
      const monthEnd = monthStart.endOf('month');
      const isMonthDisabled = disableFutureDays && monthStart.gt(today);

      const isFromSelection =
        parsedFromDate !== null &&
        parsedFromDate.getYear() === year &&
        parsedFromDate.getMonth() === month;

      const isToSelection =
        parsedToDate !== null &&
        parsedToDate.getYear() === year &&
        parsedToDate.getMonth() === month;

      const { start, end, isPreview } = activeRange;

      if (!start || !end) {
        return {
          isInRange: false,
          isStart: false,
          isEnd: false,
          isSingle: false,
          isPreview: false,
          isFromSelection,
          isToSelection,
          isDisabled: isMonthDisabled,
        };
      }

      const isInRange = start.lte(monthEnd) && end.gte(monthStart);

      if (!isInRange) {
        return {
          isInRange: false,
          isStart: false,
          isEnd: false,
          isSingle: false,
          isPreview: false,
          isFromSelection,
          isToSelection,
          isDisabled: isMonthDisabled,
        };
      }

      const isStart = start.getYear() === year && start.getMonth() === month;
      const isEnd = end.getYear() === year && end.getMonth() === month;

      return {
        isInRange: true,
        isStart,
        isEnd,
        isSingle: isStart && isEnd,
        isPreview,
        isFromSelection,
        isToSelection,
        isDisabled: isMonthDisabled,
      };
    },
    [disableFutureDays, today, parsedFromDate, parsedToDate, activeRange]
  );

  const navigateCalendars = useCallback(
    (direction: CalendarNavigationDirection) => {
      if (isAnimating) return;

      setHoveredMonth(null);
      setIsAnimating(true);
      setPrevFromYear(fromYear);
      setPrevToYear(toYear);

      setSlideDirection(direction === 'prev' ? 'right' : 'left');

      if (direction === 'prev') {
        setFromYear((prev) => {
          prevFromYearRef.current = prev - 1;
          return prev - 1;
        });
      } else {
        setFromYear((prev) => {
          prevFromYearRef.current = prev + 1;
          return prev + 1;
        });
      }

      setTimeout(() => {
        setPrevFromYear(null);
        setPrevToYear(null);
        setSlideDirection(null);
        setIsAnimating(false);
      }, 300);
    },
    [isAnimating, fromYear, toYear]
  );

  const onMonthHover = useCallback((year: number, month: number) => {
    setHoveredMonth({ year, month });
  }, []);

  const clearHoveredMonth = useCallback(() => {
    setHoveredMonth(null);
  }, []);

  const fromInputControl = useMemo(
    () => ({
      value: fromDate,
      onChange: onFromInputChange,
      onFocus: onFromInputFocus,
      onBlur: onFromInputBlur,
    }),
    [fromDate, onFromInputChange, onFromInputFocus, onFromInputBlur]
  );

  const toInputControl = useMemo(
    () => ({
      value: toDate,
      onChange: onToInputChange,
      onFocus: onToInputFocus,
      onBlur: onToInputBlur,
    }),
    [toDate, onToInputChange, onToInputFocus, onToInputBlur]
  );

  const displayText = useMemo(() => {
    if (fromDate && toDate) {
      return `${formatDateDisplay(fromDate)} - ${formatDateDisplay(toDate)}`;
    }
    if (fromDate) {
      return formatDateDisplay(fromDate);
    }
    return 'Select date range';
  }, [fromDate, toDate]);

  return {
    isOpen,
    setIsOpen,
    displayText,
    selectedRangeType,
    onPredefinedRangeChange,
    fromInputControl,
    toInputControl,
    fromError,
    toError,
    calendarState: {
      fromYear,
      toYear,
      prevFromYear,
      prevToYear,
      isAnimating,
      slideDirection,
      navigate: navigateCalendars,
      onMonthClick: onMonthClick,
      onMonthHover: onMonthHover,
      clearHoveredMonth,
      getMonthState,
    },
  };
}
